import { useState, useEffect } from 'react';
import { ProfessionalEventTemplate, professionalTemplateService } from '@/services/professionalTemplateService';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useProfessionalTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<ProfessionalEventTemplate[]>([]);
  const [publicTemplates, setPublicTemplates] = useState<ProfessionalEventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      console.log('Loading professional templates for user:', user.id);
      
      const [userTemplates, publicTemplatesData] = await Promise.all([
        professionalTemplateService.getUserTemplates(),
        professionalTemplateService.getPublicTemplates()
      ]);
      
      console.log('Fetched user templates:', userTemplates);
      console.log('Fetched public templates:', publicTemplatesData);
      
      setTemplates(userTemplates);
      setPublicTemplates(publicTemplatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [user]);

  // Set up real-time subscription for templates
  useEffect(() => {
    if (!user) return;

    console.log('Setting up real-time subscription for professional templates');
    
    const channel = supabase
      .channel('professional_templates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'professional_event_templates',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Professional templates change detected:', payload);
          loadTemplates();
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up professional templates subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  const createTemplate = async (templateData: {
    name: string;
    description?: string;
    category: string;
    event_type: string;
    client_id?: string;
    default_options: any;
    default_duration_hours?: number;
    default_reminder_days?: number;
    is_public: boolean;
  }) => {
    try {
      console.log('Creating professional template:', templateData);
      const newTemplate = await professionalTemplateService.createTemplate({
        ...templateData,
        event_type: templateData.event_type as 'voting' | 'rsvp'
      });
      console.log('Template created successfully:', newTemplate);
      setTemplates(prev => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      console.error('Error creating template:', err);
      throw err;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<ProfessionalEventTemplate>) => {
    try {
      console.log('Updating professional template:', id, updates);
      const updatedTemplate = await professionalTemplateService.updateTemplate(id, updates);
      setTemplates(prev => prev.map(template => 
        template.id === id ? updatedTemplate : template
      ));
      return updatedTemplate;
    } catch (err) {
      console.error('Error updating template:', err);
      throw err;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      console.log('Deleting professional template:', id);
      await professionalTemplateService.deleteTemplate(id);
      setTemplates(prev => prev.filter(template => template.id !== id));
    } catch (err) {
      console.error('Error deleting template:', err);
      throw err;
    }
  };

  const useTemplate = async (template: ProfessionalEventTemplate) => {
    try {
      await professionalTemplateService.incrementUsage(template.id);
      // Update local state optimistically
      setTemplates(prev => prev.map(t => 
        t.id === template.id ? { ...t, usage_count: t.usage_count + 1 } : t
      ));
      setPublicTemplates(prev => prev.map(t => 
        t.id === template.id ? { ...t, usage_count: t.usage_count + 1 } : t
      ));
    } catch (err) {
      console.error('Error incrementing template usage:', err);
      // Don't throw as this is not critical
    }
  };

  return {
    templates,
    publicTemplates,
    loading,
    error,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    useTemplate,
  };
};